import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import { isNil } from 'lodash';

import { ComponentTierName, interestedPackageEnum } from '@/constants/enums';
import { permissions } from '@/constants';
import { getDynamicComponent } from '@/constants/componentsMap';
import { useStores } from '@/hooks/use-stores';
import { InvestigationTabPane, MapBuilder, MapKPI, SpinCentered, StationControlCenter } from '@/comp';
import StationStages from '@/comp/StationStages/StationStages';
import StationReadinessScore from '@/comp/StationReadinessScore/StationReadinessScore';

import EPTierProtection from '../common/EPTierProtection';
import { EPSystemTierPaymentPlanCard } from '../common/EPTierPaymentPlanCard';
import { Container } from './Main.styles';
import MinimizedDrawer from '../MapBuilder/nodes/nodeWidget/MinimizedDrawer/MinimizedDrawer';

const Main = observer(({ tabKey = 'map' }) => {
  const { stationId } = useParams();
  const navigate = useNavigate();
  const { stationStore, mapBuilderStore, pidEditStore, permissionStore, investigationStore } = useStores();
  const { currentStationStage } = stationStore;
  const showStationScore = currentStationStage?.stage?.order < 4;
  const { t } = useTranslation();
  const isReady = getIsReady();
  const [windowIsOnFocus, setWindowIsOnFocus] = useState(false);
  const [, setWindowWidth] = useState(window.innerWidth);
  const mainContainerRef = useRef<HTMLDivElement>(null);

  const currentTier = stationStore?.stationTier;
  const Trends = getDynamicComponent(ComponentTierName.TrendsView, currentTier);

  // Reset dates1 on mount only (not when searchParams change)
  // This allows deep linking to work - InvestigationTabPane will handle setting dates from query params
  useEffect(() => {
    investigationStore.setDates1(null);
  }, []);
  const hasStationScorePermission = permissionStore.isAuth(permissions.VIEW_STATION_SCORE);

  useEffect(() => investigationStore.setDates1(null), []);

  const onChangeTab = value => {
    mapBuilderStore.closeMinimizedModal();
    navigate(`../${value}`);
  };

  function getIsReady() {
    if (!stationId) return false;
    return stationId === stationStore.currentStation.id;
  }

  // window on focus
  function setWindowIsFocus() {
    setWindowIsOnFocus(true);
  }

  function setWindowIsBlur() {
    setWindowIsOnFocus(false);
  }

  useEffect(() => {
    window.addEventListener('focus', setWindowIsFocus);
    window.addEventListener('blur', setWindowIsBlur);
    return () => {
      window.removeEventListener('focus', setWindowIsFocus);
      window.removeEventListener('blur', setWindowIsBlur);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onRequestTier = () =>
    stationStore.updateRequestTier({
      emailRequestTypeOfInterestedPackage: interestedPackageEnum.SYSTEM_CONTROL,
      emailRequestButtonClicked: 'Map KNOW - SYSTEM payment plan button',
      emailRequestWhichScreen: 'MAP',
    });

  const devicesSource = pidEditStore.isInEditMode ? stationStore.draftDevices : stationStore.currentStation.devices;

  const tabItems = [
    {
      label: t('Map'),
      key: 'map',
      children: (
        <div className="tab-map">
          <EPTierProtection componentName={ComponentTierName.PaymentPlan}>
            <>
              <div className="low-opacity-cover" />
              <span className="background-data-label">{t('backgroundDataMap')}</span>
              <EPSystemTierPaymentPlanCard onRequestTier={onRequestTier} />
            </>
          </EPTierProtection>
          <div className={stationStore.isSidebarCollapsed ? 'map-kpi-container collapsed' : 'map-kpi-container'}>
            <div className="centerer">
              <MapKPI />
            </div>
          </div>
          <div className="map-builder-container">
            {tabKey === 'map' && (
              <MapBuilder
                key={pidEditStore.isEditModeEnabled ? 'edit' : 'view'}
                windowIsOnFocus={windowIsOnFocus}
                devicesSource={devicesSource}
                isMainMap
                isEditMode={pidEditStore.isEditModeEnabled}
              />
            )}
          </div>
          {mapBuilderStore.isDetailsModalMinimized ? (
            <div className="minimized-modal-container">
              <MinimizedDrawer mainContainerRef={mainContainerRef} />
            </div>
          ) : null}
        </div>
      ),
    },
    {
      label: t('Trends'),
      key: 'trends',
      children: <Trends />,
    },
    {
      label: t('Investigation'),
      key: 'investigation',
      children: <InvestigationTabPane />,
    },
    ...(!isNil(stationStore.currentStation.stationPressureType) && !stationStore.isWasteWaterStation
      ? [
          {
            label: t('Station Control Center'),
            key: 'control-center',
            children: tabKey === 'control-center' && <StationControlCenter />,
          },
        ]
      : []),
  ];

  return (
    <Container $activeKey={tabKey} ref={mainContainerRef}>
      <StationStages />
      {hasStationScorePermission && showStationScore && <StationReadinessScore />}
      {isReady ? (
        <Suspense>
          <Tabs
            centered
            activeKey={tabKey}
            animated={{
              inkBar: true,
              tabPane: false,
            }}
            onChange={onChangeTab}
            items={tabItems}
          />
        </Suspense>
      ) : (
        <SpinCentered />
      )}
    </Container>
  );
});

export default Main;
